<template>
  <AModal
    :model-value="modelValue"
    size="lg"
    title="data-id 맵핑 관리"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <template #sub>
      <span class="tm-sub">
        <i class="material-icons-round">qr_code_2</i>
        바코드: <strong>{{ barcode || "-" }}</strong>
      </span><br />
      <p class="tm__guide">
        교재 콘텐츠의 <code>data-id</code>(예: <code>beg1-l1-nwe-ex1</code>)의 한 구간을 위치명으로
        변환하기 위한 맵핑입니다.
        <strong>depth</strong>는 계층 레벨, <strong>키</strong>는 해당 구간 문자열(예: <code>beg1</code>
        또는 <code>beg1-l1</code>처럼 <code>-</code>를 포함할 수 있음), <strong>값</strong>은 사람이 읽는 위치명입니다.
        여러 행을 작성하거나 엑셀로 업로드한 뒤 <strong>한 번에 저장</strong>할 수 있습니다.
      </p><br />
      <!-- 툴바 -->
      <div class="tm__toolbar">
        <div class="tm__toolbar-left">
          <ABtn kind="ghost" size="sm" icon="add" @click="addRow">행 추가</ABtn>
          <ABtn kind="ghost" size="sm" icon="upload_file" @click="triggerExcelUpload">엑셀 업로드</ABtn>
          <ABtn kind="ghost" size="sm" icon="download" @click="downloadTemplate">엑셀 양식</ABtn>
          <!-- 전체 삭제 와 전체 다운로드 -->
          <ABtn
            kind="ghost"
            size="sm"
            icon="file_download"
            :disabled="!rows.length"
            @click="downloadAll"
          >전체 다운로드</ABtn>
          <ABtn
            kind="ghost"
            size="sm"
            icon="delete_sweep"
            :disabled="!rows.length"
            @click="clearAll"
          >전체 삭제</ABtn>
          <input
            ref="excelInput"
            type="file"
            accept=".xlsx,.xls,.csv"
            class="tm__file"
            @change="handleExcelUpload"
          />
        </div>
        <span class="tm__count">{{ rows.length }}개 행</span>
      </div>
    </template>

    <div class="tm">
      <!-- 편집 그리드 -->
      <div class="tm__grid">
        <div class="tm__grid-head">
          <span>depth</span>
          <span>키(key)</span>
          <span>값(value)</span>
          <span></span>
        </div>

        <div
          v-for="(row, i) in rows"
          :key="row._uid"
          class="tm__grid-row"
          :class="{ 'is-new': row.idx == null }"
        >
          <input
            v-model.number="row.depth"
            type="number"
            min="0"
            class="p-input"
            :class="{ 'is-error': row._err && row._err.depth }"
            placeholder="0"
          />
          <input
            v-model.trim="row.key"
            type="text"
            class="p-input tm__mono"
            :class="{ 'is-error': row._err && row._err.key }"
            placeholder="예: nwe"
          />
          <input
            v-model.trim="row.value"
            type="text"
            class="p-input"
            :class="{ 'is-error': row._err && row._err.value }"
            placeholder="예: New Words and Expressions"
          />
          <button type="button" class="tm__row-del" title="행 삭제" @click="removeRow(i)">
            <i class="material-icons-round">close</i>
          </button>
        </div>

        <div v-if="!rows.length" class="tm__grid-empty">
          행이 없습니다. <strong>[행 추가]</strong> 또는 <strong>[엑셀 업로드]</strong>로 추가하세요.
        </div>
      </div>
    </div>

    <template #foot>
      <ABtn kind="ghost" @click="$emit('update:modelValue', false)">닫기</ABtn>
      <ABtn kind="yellow" icon="save" :loading="saving" :disabled="loadError" @click="onSave">
        전체 저장 ({{ rows.length }})
      </ABtn>
    </template>
  </AModal>
</template>

<script>
import * as XLSX from "xlsx";
import AModal from "@/components/Pineapple/AModal.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";

export default {
  name: "TextbookMappingModal",
  components: { AModal, ABtn },
  props: {
    modelValue: { type: Boolean, default: false },
    barcode: { type: String, default: "" },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      rows: [],
      saving: false,
      loadError: false,
      uidSeq: 0,
    };
  },
  watch: {
    modelValue(open) {
      if (open) this.getMappings();
    },
  },
  methods: {
    makeRow(data = {}) {
      this.uidSeq += 1;
      return {
        _uid: this.uidSeq,
        idx: data.idx ?? null,
        depth: data.depth ?? 0,
        key: data.key ?? "",
        value: data.value ?? "",
        _err: null,
      };
    },
    async getMappings() {
      this.loadError = false;
      if (!this.barcode) {
        this.rows = [];
        return;
      }
      try {
        const res = await this.$axios({
          method: "get",
          url: `/textbook/editor/textbookContents/${this.barcode}/mappings`,
          loadingBar: true,
        });
        this.rows = res.status === 200 && Array.isArray(res.data)
          ? res.data.map((m) => this.makeRow(m))
          : [];
      } catch (error) {
        console.error("Error :", error);
        this.rows = [];
        this.loadError = true;
        this.$notifyVue({ type: "danger", title: "맵핑 목록을 불러오지 못했습니다." });
      }
    },
    addRow() {
      this.rows.push(this.makeRow());
    },
    removeRow(index) {
      this.rows.splice(index, 1);
    },
    triggerExcelUpload() {
      this.$refs.excelInput.click();
    },
    async handleExcelUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      try {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data);
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        let added = 0;
        let updated = 0;
        jsonData.forEach((row, idx) => {
          if (!row || row.length === 0) return;
          // 헤더 행 스킵 (depth/key/value 라벨)
          const first = row[0] != null ? String(row[0]).trim().toLowerCase() : "";
          if (idx === 0 && (first === "depth" || first === "" )) return;

          const depthRaw = row[0];
          const key = row[1] != null ? String(row[1]).trim() : "";
          const value = row[2] != null ? String(row[2]).trim() : "";
          if (key === "" && value === "") return;

          const depthNum = isNaN(Number(depthRaw)) ? 0 : Number(depthRaw);
          // 같은 (depth, key) 가 이미 있으면 값만 갱신(중복 행 누적 방지 → PUT 409 예방)
          const existing = this.rows.find(
            (r) => Number(r.depth) === depthNum && String(r.key).trim() === key
          );
          if (existing) {
            existing.value = value;
            updated += 1;
          } else {
            this.rows.push(this.makeRow({ depth: depthNum, key, value }));
            added += 1;
          }
        });

        if (added > 0 || updated > 0) {
          this.$notifyVue({ type: "success", title: `추가 ${added}건 · 갱신 ${updated}건`, text: "저장 버튼을 눌러 반영하세요." });
        } else {
          this.$notifyVue({ type: "warning", title: "추가할 데이터가 없습니다." });
        }
      } catch (error) {
        console.error("Excel 파일 처리 오류:", error);
        this.$notifyVue({ type: "danger", title: "엑셀 파일을 읽는 중 오류가 발생했습니다.", text: "파일 형식을 확인해주세요." });
      } finally {
        event.target.value = "";
      }
    },
    downloadTemplate() {
      const aoa = [
        ["depth", "key", "value"],
        [1, "beg1", "Beginning 1"],
        [2, "beg1-l1", "Lesson 1"],
        [3, "beg1-l1-nwe", "New Words and Expressions"],
        [4, "beg1-l1-nwe-ex1", "Exercise 1"],
      ];
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "맵핑");
      XLSX.writeFile(wb, `data-id_맵핑_양식_${this.barcode || "교재"}.xlsx`);
    },
    // 현재 그리드의 전체 맵핑을 엑셀로 내려받기 (양식과 동일한 depth/key/value 구조)
    downloadAll() {
      if (!this.rows.length) return;
      const aoa = [
        ["depth", "key", "value"],
        ...this.rows.map((r) => [Number(r.depth) || 0, r.key, r.value]),
      ];
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "맵핑");
      XLSX.writeFile(wb, `data-id_맵핑_${this.barcode || "교재"}.xlsx`);
    },
    // 그리드의 모든 행 비우기 (실제 삭제는 [전체 저장] 시 빈 목록 PUT 으로 반영)
    async clearAll() {
      if (!this.rows.length) return;
      const confirmation = await this.$swal({
        title: "전체 삭제",
        text: `${this.rows.length}개 행을 모두 비웁니다. [전체 저장]을 눌러야 실제로 반영됩니다.`,
        icon: "warning",
        showConfirmButton: true,
        showCancelButton: true,
        confirmButtonText: this.$t("btn.22"),
        cancelButtonText: this.$t("btn.23"),
      });
      if (!confirmation.isConfirmed) return;
      this.rows = [];
      this.$notifyVue({ type: "success", title: "전체 행을 비웠습니다.", text: "저장 버튼을 눌러 반영하세요." });
    },
    // 반환: "" (정상) | "required" (필수값 누락) | "dup" (depth+key 중복)
    validate() {
      let missing = false;
      this.rows.forEach((row) => {
        const err = { depth: false, key: false, value: false };
        if (row.depth === "" || row.depth == null || isNaN(row.depth) || row.depth < 0) {
          err.depth = true;
          missing = true;
        }
        if (!row.key) {
          err.key = true;
          missing = true;
        }
        if (!row.value) {
          err.value = true;
          missing = true;
        }
        row._err = err;
      });

      // 중복 (depth, key) 검사 — 백엔드 유니크 제약 위반(409) 예방
      let dup = false;
      const seen = new Map();
      this.rows.forEach((row) => {
        if (row.depth === "" || row.depth == null || isNaN(row.depth) || !row.key) return;
        const k = Number(row.depth) + "|" + String(row.key).trim();
        if (seen.has(k)) {
          dup = true;
          row._err = { ...row._err, depth: true, key: true };
          const firstRow = seen.get(k);
          firstRow._err = { ...firstRow._err, depth: true, key: true };
        } else {
          seen.set(k, row);
        }
      });

      if (missing) return "required";
      if (dup) return "dup";
      return "";
    },
    async onSave() {
      if (this.loadError) {
        this.$notifyVue({ type: "warning", title: "목록을 먼저 다시 불러오세요." });
        return;
      }
      const validation = this.validate();
      if (validation === "required") {
        this.$notifyVue({ type: "warning", title: "입력값을 확인하세요.", text: "표시된 행의 depth/키/값을 채워주세요." });
        return;
      }
      if (validation === "dup") {
        this.$notifyVue({
          type: "warning",
          title: "중복된 맵핑이 있습니다.",
          text: "같은 depth·키 조합이 중복되었습니다. 표시된 행을 정리해 주세요.",
        });
        return;
      }
      // 전체 교체(PUT): 현재 그리드가 곧 저장될 전체 맵핑 상태
      // (depth+key 중복은 위 validate 에서 차단 → 백엔드 유니크 제약 위반 방지)
      const payload = {
        mappings: this.rows.map((r) => ({
          depth: Number(r.depth),
          key: r.key,
          value: r.value,
        })),
      };
      const requestFn = async () => {
        return await this.$axios({
          method: "put",
          url: `/textbook/editor/textbookContents/${this.barcode}/mappings`,
          data: payload,
        });
      };
      this.saving = true;
      const result = await this.$showConfirmationAndSubmit(requestFn, null, {
        title: this.$t("alert.47"),
        text: `${this.rows.length}개의 맵핑을 저장합니다.`,
      });
      this.saving = false;
      if (result === "success") {
        this.$notifyVue({ title: this.$t("alert.18"), text: "Saved successfully", type: "success" });
        this.$emit("update:modelValue", false);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.tm-sub {
  display: inline-flex;
  align-items: center;
  gap: 6px;

  > i { font-size: 15px; }
  strong { color: $p-ink; font-family: $p-font-mono; }
}

.tm {
  display: flex;
  flex-direction: column;
  gap: 14px;

  &__guide {
    margin: 0;
    padding: 12px 14px;
    background: $p-bg;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    font-size: 12px;
    line-height: 1.6;
    color: $p-ink-mid;

    code {
      font-family: $p-font-mono;
      background: $p-panel-alt;
      padding: 1px 5px;
      border-radius: $p-radius-sm;
      color: $p-ink;
    }
    strong { color: $p-ink; }
  }

  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  &__toolbar-left {
    display: inline-flex;
    gap: 6px;
    flex-wrap: wrap;
  }

  &__file { display: none; }

  &__count {
    font-size: 12px;
    font-weight: 700;
    color: $p-ink-mid;
    font-family: $p-font-mono;
  }

  &__grid {
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    overflow: hidden;
  }

  &__grid-head,
  &__grid-row {
    display: grid;
    grid-template-columns: 90px 200px 1fr 36px;
    gap: 8px;
    align-items: center;
    padding: 8px 10px;
  }

  &__grid-head {
    background: $p-bg;
    border-bottom: 1px solid $p-line;

    > span {
      font-size: 10px;
      font-weight: 800;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: $p-ink-soft;
    }
  }

  &__grid-row {
    border-bottom: 1px solid $p-line-soft;

    &:last-child { border-bottom: 0; }

    &.is-new { background: $p-point-soft; }

    .p-input.is-error { border-color: $p-err; }
  }

  &__mono { font-family: $p-font-mono; }

  &__row-del {
    width: 28px;
    height: 28px;
    border: 0;
    background: transparent;
    color: $p-ink-mid;
    border-radius: $p-radius-sm;
    display: grid;
    place-items: center;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease;

    > i { font-size: 16px; }
    &:hover { background: $p-err-soft; color: $p-err; }
  }

  &__grid-empty {
    padding: 32px 14px;
    text-align: center;
    color: $p-ink-soft;
    font-size: 12px;

    strong { color: $p-ink-mid; }
  }
}
</style>
